import { create } from 'zustand'

const CACHED_PERMS_KEY = 'auth-perms-cache'

/** Read cached permissions from localStorage (no PII, just permission strings) */
function getCachedPermissions(): string[] {
  try {
    const raw = localStorage.getItem(CACHED_PERMS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

/** Write permissions to localStorage */
function setCachedPermissions(perms: string[]) {
  try { localStorage.setItem(CACHED_PERMS_KEY, JSON.stringify(perms)) } catch {}
}

/** Clear cached permissions (on logout) */
function clearCachedPermissions() {
  try { localStorage.removeItem(CACHED_PERMS_KEY) } catch {}
}
/** Page-tour status as stored server-side (preferences.tours[tourId]). */
export type ServerTourStatus = 'offered' | 'started' | 'completed' | 'skipped' | 'dismissed'
export interface ServerTourEntry {
  s: ServerTourStatus
  v: number
  at: string | null
}

/** First-time welcome card answer, stored in t_iam_users.preferences.onboarding.welcome. */
export type WelcomeAnswer = 'dismissed' | 'started' | 'completed'

export interface AuthUser {
  id: string
  email: string
  first_name: string
  last_name: string
  status: string
  active_client: { id: string; name: string }
  client_ids: string[]
  clients?: { id: string; name: string }[]
  roles: { id: string; name: string; display_name?: string; is_template: boolean }[]
  permissions: string[]
  /** Queue bucket slugs the user is allowed to see, from t_iam_role_buckets. */
  buckets?: string[]
  /** Access-request types this user may file (server rule, `/auth/me`). */
  access_request_types?: string[]
  facilities?: { id: string; name: string; client_id: string; client_name: string }[]
  is_impersonating?: boolean
  real_actor?: { id: string }
  /** ISO deadline when impersonation auto-expires (cap); drives the banner countdown. */
  impersonation_expires_at?: string
  /**
   * First-time welcome card (server-computed): eligible iff never answered and
   * not impersonating. `welcome` is the stored answer. Absent on old sessions.
   */
  onboarding?: {
    welcome_eligible: boolean
    welcome: WelcomeAnswer | null
    /** Page-tour invitations allowed (false when opted out or impersonating). */
    page_invites?: boolean
    /** Server page-tour statuses (preferences.tours), keyed by stable tour id. */
    tours?: Record<string, ServerTourEntry>
  }
  /**
   * Server-driven feature payload from /auth/me.
   *   • announcements_module — legacy env-driven flag, kept for existing consumers.
   *   • vectors — per-client { feature_key: value } map (scope='all' user gets
   *     one Master vector; scope='own' user gets one per accessible client).
   *   • defaults — catalog defaults keyed by feature_key, used when a vector
   *     row is absent for the resolved target client.
   * Use `useFeature(key, resourceClientId?)` (shared/entitlements) — do not
   * read this shape directly.
   */
  features?: {
    announcements_module?: boolean
    /** Support Codio entry (header user menu): enabled, eligible on the active client, not impersonating. */
    support_codio?: boolean
    vectors?: Record<string, Record<string, unknown>>
    defaults?: Record<string, unknown>
    /**
     * Per-facility effective values for the user's assigned facilities, keyed
     * by facility_id → { feature_key: value }. Only facility-scoped keys. Read
     * via useFeature(key, client, facilityId).
     */
    facilityVectors?: Record<string, Record<string, unknown>>
  }
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  isMfaRequired: boolean
  /**
   * True once the initial auth bootstrap (silent refresh + /auth/me) has settled,
   * regardless of outcome. Components that depend on auth context (e.g. data
   * polling, protected routes) must not mount until this flips true — otherwise
   * they fire requests with no Authorization header and hit a hard 401.
   */
  isBootstrapped: boolean
  /** Permissions from previous session, used to render sidebar while auth is loading */
  cachedPermissions: string[]

  setUser: (user: AuthUser) => void
  clearUser: () => void
  setLoading: (loading: boolean) => void
  setBootstrapped: (value: boolean) => void
  setMfaRequired: (required: boolean) => void

  /**
   * Check if user has a specific permission string.
   * Supports exact match: "coder_workspace:view:own"
   */
  hasPermission: (permission: string) => boolean

  /**
   * 3-dimensional permission check: module, action, scope
   * e.g., hasPermission3d('coder_workspace', 'view', 'own')
   */
  hasPermission3d: (module: string, action: string, scope?: string) => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isBootstrapped: false,
  isMfaRequired: false,
  cachedPermissions: getCachedPermissions(),

  setUser: (user) => {
    setCachedPermissions(user.permissions)
    set({ user, isAuthenticated: true, isLoading: false, isBootstrapped: true, isMfaRequired: false, cachedPermissions: user.permissions })
  },

  clearUser: () => {
    clearCachedPermissions()
    set({ user: null, isAuthenticated: false, isLoading: false, isBootstrapped: true, isMfaRequired: false, cachedPermissions: [] })
  },

  setLoading: (isLoading) => set({ isLoading }),

  setBootstrapped: (isBootstrapped) => set({ isBootstrapped }),

  setMfaRequired: (isMfaRequired) => set({ isMfaRequired }),

  hasPermission: (permission: string) => {
    const { user } = get()
    if (!user) return false
    return user.permissions.includes(permission)
  },

  hasPermission3d: (module: string, action: string, scope?: string) => {
    const { user } = get()
    if (!user) return false
    if (!scope) {
      const prefix = `${module}:${action}`
      return user.permissions.some((p) => p.startsWith(prefix))
    }
    // Scope hierarchy: a broader scope satisfies a narrower check.
    //
    // These are the ONLY two scopes the permission catalog defines
    // (t_iam_permissions: 37 `all` rows, 36 `own` rows — verified 2026-09-04).
    // The map previously read `{ own: 1, team: 2, client: 3, all_clients: 4 }`,
    // three of which never existed, and — critically — omitted `all`. With
    // `all` missing, `scopeLevel['all'] ?? 0` drove requiredLevel to 0, so
    // every `…, 'all')` gate silently degraded into "holds ANY grant for this
    // module:action": client_admin (own-scope only) passed the
    // biller-super-admin gate on the Catalog page. The inverse was just as
    // wrong — an `all` holder scored 0 and failed an `own` check.
    //
    // An UNKNOWN scope must never satisfy a gate, so it scores -1 on the left
    // of the comparison and Infinity on the right: unrecognised required scope
    // admits nobody, unrecognised held scope satisfies nothing.
    const scopeLevel: Record<string, number> = { own: 1, all: 2 }
    const requiredLevel = scopeLevel[scope] ?? Number.POSITIVE_INFINITY
    const prefix = `${module}:${action}:`
    return user.permissions.some((p) => {
      if (!p.startsWith(prefix)) return false
      const permScope = p.slice(prefix.length)
      return (scopeLevel[permScope] ?? -1) >= requiredLevel
    })
  },
}))
